import React from 'react';

import { AddOnDisclaimer } from '@/booking-add-ons';
import { IBookingInstance } from '@/booking/models';
import { IHotelModelInstance } from '@/hotel';
import { useLocale, useTranslation } from '@/i18n';
import { Divider, LoadingCard } from '@/ui/layout';
import { AnimatedStack, Spacer } from '@/ui/spacing';
import { useTheme } from '@/ui/theme';
import { BodySmall, Heading } from '@/ui/typography';

import { useAvailableAddOns, useUpdateBookingAddOn } from '../hooks';
import { AddOnWithCheckbox } from './AddOnWithCheckbox.component';
import { AddOnWithRocker } from './AddOnWithRocker.component';
import { useActiveBrandConfig } from '@/brand';

export const BookingAddOnSelector = ({
  hotel,
  booking,
}: {
  hotel: IHotelModelInstance;
  booking: IBookingInstance;
}) => {
  const locale = useLocale();
  const { updateAddOn, addOnBeingUpdated } = useUpdateBookingAddOn();
  const { t: additionalInfoTranslation } = useTranslation(
    'additionalInformation'
  );
  const { showAddonDisclaimer } = useActiveBrandConfig();

  const {
    forms,
    componentProperties: { addOnList },
  } = useTheme();

  const { data: addOns } = useAvailableAddOns({
    hotelReferenceId: hotel.referenceId,
    bookingId: booking.id,
    locale: { language: locale.baseName },
  });

  if (!addOns)
    return (
      <>
        <LoadingCard>
          <BodySmall>
            {additionalInfoTranslation('addOns.status.loadingAddOns')}
          </BodySmall>
        </LoadingCard>
        <Spacer s="m" />
      </>
    );

  if (addOns.length === 0) return null;

  return (
    <>
      {addOnList?.topDivider && <Divider css={addOnList?.topDivider} />}
      <Heading as="h6" style="heading6" id="addOnsTitle" css={forms.heading}>
        {additionalInfoTranslation('addOns.title')}
      </Heading>
      {showAddonDisclaimer && (
        <>
          <Spacer s="m" />
          <AddOnDisclaimer
            bookingId={booking.id}
            hotelReferenceId={hotel.referenceId}
          />
        </>
      )}
      <Spacer s="m" />
      <AnimatedStack as="ul" s="m" aria-labelledby="addOnsTitle">
        {addOns.map((addOn) => (
          <>
            {(() => {
              switch (addOn.displayType) {
                case 'ADD_ON_DISPLAY_TYPE_TOGGLE':
                  return (
                    <AddOnWithCheckbox
                      key={addOn.id}
                      addOn={addOn}
                      bookingId={booking.id}
                      hotelReferenceId={hotel.referenceId}
                      currentAddOns={booking.addOns}
                      isLoading={addOnBeingUpdated === addOn.id}
                      isDisabled={
                        addOnBeingUpdated !== undefined &&
                        addOnBeingUpdated !== addOn.id
                      }
                      updateAddOn={updateAddOn}
                    />
                  );
                case 'ADD_ON_DISPLAY_TYPE_ROCKER':
                  return (
                    <AddOnWithRocker
                      key={addOn.id}
                      addOn={addOn}
                      bookingId={booking.id}
                      hotelReferenceId={hotel.referenceId}
                      currentAddOns={booking.addOns}
                      isLoading={addOnBeingUpdated === addOn.id}
                      isDisabled={
                        addOnBeingUpdated !== undefined &&
                        addOnBeingUpdated !== addOn.id
                      }
                      updateAddOn={updateAddOn}
                    />
                  );
                case 'ADD_ON_DISPLAY_TYPE_UNKNOWN':
                default:
                  return null;
              }
            })()}
          </>
        ))}
      </AnimatedStack>
      <Spacer s="xl" />
    </>
  );
};
